import { Action, createReducer, on } from '@ngrx/store';
import { TicketType, TicketTypeJobTitle, TicketTypeRole, TicketTypeUser, ViewType } from 'src/app/models/ticketTypeModels';
import { saveTicketUserAssignments, saveTicketRoleAssignments, saveTicketJobTitleAssignments, saveTicketAssignmentsFailure } from './ticket-types.actions';
import { loadTicketTypes, loadTicketTypesSuccess, 
  loadTicketTypesFailure, loadTicketType, loadTicketTypeSuccess,
  loadTicketTypeFailure, createTicketType, createTicketTypeSuccess,
  createTicketTypeFailure, updateTicketType, updateTicketTypeSuccess, 
  updateTicketTypeFailure, deleteTicketType, deleteTicketTypeSuccess,
  deleteTicketTypeFailure, reorderStatusesSuccess, getViewTypesSuccess, 
  filterTicketUserAssignmentsSuccess, 
  filterTicketRoleAssignmentsSuccess,
  filterTicketJobTitleAssignmentsSuccess,
  saveTicketUserAssignmentsSuccess,
  saveTicketRoleAssignmentsSuccess,
  saveTicketJobTitleAssignmentsSuccess,
  getFieldTypesSuccess,
  doUpdateSelectedTicketType,
  getProgramListSuccess} from './ticket-types.actions';

export interface TicketTypesState {
  ticketTypes: TicketType[];
  includeArchived: boolean;
  viewTypes: ViewType[];
  fieldTypes: ViewType[];
  programs: any[],
  selectedTicketType: TicketType | null;
  error: any;
  loading: boolean;
  saving: boolean;
  selectedTicketTypeId: number | null;
  userAssignments: TicketTypeUser[]; 
  roleAssignments: TicketTypeRole[]; 
  jobTitleAssignments: TicketTypeJobTitle[]; 
}

export const initialState: TicketTypesState = {
  ticketTypes: [],
  includeArchived: false,
  viewTypes: [],
  fieldTypes: [],
  programs: [],
  selectedTicketType: null,
  error: null,
  loading: false,
  saving: false,
  selectedTicketTypeId: null,
  userAssignments: [],
  roleAssignments: [],
  jobTitleAssignments: [], 
};

export const ticketTypesReducer = createReducer(
  initialState,
  on(loadTicketTypes, (state, { includeArchived }) => ({
    ...state,
    includeArchived,
    loading: true,
  })),
  on(loadTicketTypesSuccess, (state, { ticketTypes, includeArchived }) => state.includeArchived !== includeArchived ? state : ({
    ...state,
    ticketTypes,
    loading: false,
  })),
  on(getProgramListSuccess, (state, { programs }) => ({
    ...state,
    programs,
    loading: false,
  })),
  on(loadTicketTypesFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(loadTicketType, (state, { id }) => ({
    ...state,
    selectedTicketTypeId: id,
    selectedTicketType: null,
    loading: true,
  })),
  on(loadTicketTypeSuccess, (state, { ticketType }) => ({
    ...state,
    selectedTicketType: state.selectedTicketTypeId === ticketType.id ? ticketType : state.selectedTicketType,
    loading: false,
  })),
  on(loadTicketTypeFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(createTicketType, state => ({
    ...state,
    saving: true,
    error: null,
    loading: true,
  })),
  on(doUpdateSelectedTicketType, (state, { ticketType }) => ({
    ...state,
    selectedTicketType: ticketType,
  })),
  on(createTicketTypeSuccess, (state, { ticketType }) => ({
    ...state,
    ticketTypes: [...state.ticketTypes, ticketType],
    saving: false,
    loading: false,
  })),
  on(createTicketTypeFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
    loading: false,
  })),
  on(updateTicketType, state => ({
    ...state,
    saving: true,
    error: null,
    loading: true,
  })),
  on(updateTicketTypeSuccess, (state, { ticketType }) => ({
    ...state,
    selectedTicketType: state.selectedTicketTypeId === ticketType.id ? ticketType : state.selectedTicketType,
    ticketTypes: state.ticketTypes.map(t => t.id === ticketType.id ? ticketType : t),
    saving: false,
    loading: false,
  })),
  on(updateTicketTypeFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
    loading: false,
  })),
  on(deleteTicketType, state => ({
    ...state,
    saving: true,
    error: null,
    loading: true,
  })),
  on(deleteTicketTypeSuccess, (state, { ticketType }) => ({
    ...state,
    ticketTypes: state.ticketTypes.map(t => t.id === ticketType.id ? ticketType : t)
      .filter(t => state.includeArchived || !t.archivedDate),
    selectedTicketType: state.selectedTicketTypeId === ticketType.id ? ticketType : state.selectedTicketType,
    saving: false,
    loading: false,
  })),
  on(deleteTicketTypeFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
    loading: false,
  })),
  on(getViewTypesSuccess, (state, { viewTypes }) => ({
    ...state,
    viewTypes,
    loading: false,
  })),
  on(getFieldTypesSuccess, (state, { viewTypes }) => ({
    ...state,
    fieldTypes: viewTypes,
    loading: false,
  })),
  on(filterTicketUserAssignmentsSuccess, (state, { userAssignments }) => ({
    ...state,
    userAssignments,
    loading: false,
  })),
  on(filterTicketRoleAssignmentsSuccess, (state, { roleAssignments }) => ({
    ...state,
    roleAssignments,
    loading: false,
  })),
  on(filterTicketJobTitleAssignmentsSuccess, (state, { jobTitleAssignments }) => ({
    ...state,
    jobTitleAssignments,
    loading: false,
  })),
  on(saveTicketUserAssignments, saveTicketRoleAssignments, saveTicketJobTitleAssignments, state => ({
    ...state,
    saving: true,
    error: null,
  })),
  on(saveTicketAssignmentsFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
  })),
  on(saveTicketUserAssignmentsSuccess, (state, { userAssignments }) => ({
    ...state,
    saving: false,
    userAssignments,
    loading: false,
  })),
  on(saveTicketRoleAssignmentsSuccess, (state, { roleAssignments }) => ({
    ...state,
    saving: false,
    roleAssignments,
    loading: false,
  })),
  on(saveTicketJobTitleAssignmentsSuccess, (state, { jobTitleAssignments }) => ({
    ...state,
    saving: false,
    jobTitleAssignments,
    loading: false,
  })),
);

export function ticketTypesStateReducerFunc(state: TicketTypesState | undefined, action: Action) {
  return ticketTypesReducer(state, action);
}
